/** The small one-line "Changed" chip on the row a demo change touched (#2824). The reason shows in the guide card. */
export function DemoChangedTag({
  pending = false,
}: {
  /** The visitor has not made the change yet. */
  pending?: boolean;
}) {
  return (
    <span
      className="inline-flex items-center whitespace-nowrap rounded-full bg-accent-soft px-2 py-0.5 text-xs font-semibold text-accent"
      data-testid="demo-changed-tag"
    >
      {pending ? "To make" : "Changed"}
    </span>
  );
}

/** Soft ring and tint for the marked row. */
export const DEMO_CHANGED_CLASS = "ring-2 ring-accent ring-inset bg-accent-soft/70";

/** The `data-tour` anchor of the field a demo change touched, inside the opened detail. */
export const DEMO_CHANGED_FIELD_ANCHOR = "demo-changed-field";

/**
 * The `data-tour` anchor of the real control a demo visitor uses next to make
 * the picked change (#2824): a person's level or station chips, a station's
 * Floor / session field, Review & apply, Apply. It moves as the change goes on.
 */
export const DEMO_CHANGE_CONTROL_ANCHOR = "demo-change-control";

/** The `data-tour` anchor of the real People "More" button while the demo has a person to add (#2824). */
export const PEOPLE_ADD_MEMBER_ANCHOR = "people-add-member";

/**
 * Lights the one field a demo change touched inside a person card or a station
 * drawer (#2824), with the "Changed" chip. Off: the children render as they are. `primary` is the field the
 * guide points at. A second lit field of the same change sets `primary` false
 * and `bare` true, so only one field carries the anchor and the chip.
 */
export function DemoChangedField({
  on,
  primary = true,
  pending = false,
  children,
}: {
  on: boolean;
  pending?: boolean;
  primary?: boolean;
  children: React.ReactNode;
}) {
  if (!on) return <>{children}</>;
  return (
    <div
      className={`rounded-md p-2 ${DEMO_CHANGED_CLASS}`}
      data-tour={primary ? DEMO_CHANGED_FIELD_ANCHOR : undefined}
      data-demo-changed-field="true"
      data-testid="demo-changed-field"
    >
      {children}
      {primary ? (
        <div className="mt-2">
          <DemoChangedTag pending={pending} />
        </div>
      ) : null}
    </div>
  );
}
